import { useRef, useState, useEffect, useCallback } from "react";
import { useFps } from "@/contexts/FpsContext";
import { VideoControlsTimeline } from "./Timeline";
import { PlayPauseButton } from "./PlayPauseButton";
import { TimeDisplay } from "./TimeDisplay";
import { VideoSpeedControl } from "./VideoSpeedControl";
import { VideoControlsMenu } from "./VideoControlsMenu";
import { VideoControls as VideoControlsProps } from "@/contexts/FilePreviewContext";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";

const VideoControls = ({
    controls,
    flowLocked
}: {
    controls: VideoControlsProps | null;
    flowLocked: boolean;
}) => {
    const {
        videoRef,
        mediaItem,
        isPlaying = false,
        onFramePreviewsReady,
        onPlayClicked,
        onPauseClicked,
        onTimelineClick,
        onClockTick
    } = controls || {};

    const [currentTime, setCurrentTime] = useState<number | undefined>(undefined);
    const [duration, setDuration] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isVideoReadyForSeeking, setIsVideoReadyForSeeking] = useState(false);

    const hoverThrottleRef = useRef<number | null>(null);
    const mediaItemIdRef = useRef<string | undefined>(undefined);
    const targetTimeRef = useRef<number>(0); // Track target time for seeking
    const lastSeekTimeRef = useRef<number>(0); // Track last seek to prevent premature syncing
    const tickRef = useRef<string | undefined>(undefined);

    const videoFps = mediaItem?.metadata?.videoMetadata?.fps || 30;

    useEffect(() => {
        mediaItemIdRef.current = mediaItem?.id;
    }, [mediaItem]);

    const onClockTickRef = useRef(onClockTick);

    useEffect(() => {
        onClockTickRef.current = onClockTick;
    }, [onClockTick]);

    // Note: onClockTick is now called directly from Video.tsx via requestVideoFrameCallback
    // We don't need to call it here anymore to avoid double processing

    const togglePlayPause = useCallback(async () => {
        // Block all interactions during model generation
        if (flowLocked) {
            console.log("🚫 [PLAY/PAUSE] Blocked during model generation");
            return;
        }

        const video = videoRef?.current;
        if (!video) {
            console.log("🎮 [PLAY/PAUSE] No video element available");
            return;
        }

        // Simple, clean logic: toggle the state and let the video element follow
        if (isPlaying) {
            video.pause();
        } else {
            try {
                await video.play();
            } catch (e) {
                console.error("🎮 [PLAY/PAUSE] Error attempting to play video:", e);
            }
        }
    }, [flowLocked, videoRef, isPlaying]);

    const skipTime = useCallback(
        (seconds: number) => {
            if (flowLocked) {
                console.log("🚫 [SKIP TIME] Blocked during model generation");
                return;
            }

            const video = videoRef?.current;
            if (!video) {
                console.log("🎮 [SKIP TIME] No video element available");
                return;
            }

            // If targetTimeRef is uninitialized (0), sync it once
            if (targetTimeRef.current === 0 && video.currentTime !== 0) {
                targetTimeRef.current = video.currentTime;
            }

            // Use targetTimeRef to calculate new time, not video.currentTime
            // This prevents out-of-order seeks when pressing arrow keys rapidly
            const newTime = Math.max(0, Math.min(targetTimeRef.current + seconds, video.duration));
            targetTimeRef.current = newTime;
            video.currentTime = newTime;
            setCurrentTime(newTime);

            // Mark when this seek happened to prevent handleTimeUpdate from overwriting
            lastSeekTimeRef.current = Date.now();

            // Only trigger frame update when video is paused to avoid lag during playback
            if (!isPlaying) {
                onTimelineClick?.(newTime);
            }
        },
        [flowLocked, videoRef, setCurrentTime, onTimelineClick, isPlaying]
    );

    // Global hotkey: Space toggles play/pause
    useKeyboardShortcut({ key: [" ", "space"], handler: togglePlayPause });

    // Global hotkeys: Left/Right arrows skip 1s (won't conflict with Cmd+Arrow for page nav)
    useKeyboardShortcut({ key: "ArrowLeft", handler: () => skipTime(-1) });
    useKeyboardShortcut({ key: "ArrowRight", handler: () => skipTime(1) });

    const initializeVideo = useCallback(async () => {
        const video = videoRef?.current;
        if (!video) return;

        try {
            const originalTime = video.currentTime;

            // Ensure video can seek by playing briefly
            video.muted = true; // Ensure muted for autoplay
            await video.play();

            // Small seek test to ensure seeking works
            if (video.duration > 1) {
                video.currentTime = Math.min(0.1, video.duration * 0.1);
                await new Promise((resolve) => {
                    const handleSeeked = () => {
                        video.removeEventListener("seeked", handleSeeked);
                        resolve(void 0);
                    };
                    video.addEventListener("seeked", handleSeeked);
                    // Fallback timeout
                    setTimeout(resolve, 500);
                });
            }

            video.currentTime = originalTime;
            setIsVideoReadyForSeeking(true);

            // Initialize targetTimeRef with the current video time
            targetTimeRef.current = originalTime;
        } catch (error) {
            console.warn("Video initialization failed:", error);
            // Still mark as ready to allow seeking attempts
            setIsVideoReadyForSeeking(true);
        }
    }, [videoRef, setIsVideoReadyForSeeking]);

    const syncTimeUpdate = useCallback((newTime: number) => {
        setCurrentTime(newTime);
        // Only sync targetTimeRef if we haven't seeked recently (500ms)
        if (Date.now() - lastSeekTimeRef.current > 500) {
            targetTimeRef.current = newTime;
        }
    }, []);

    const handleLoadedMetadata = useCallback(() => {
        const video = videoRef?.current;
        if (!video) return;

        setDuration(video.duration);
        setCurrentTime(video.currentTime);

        // More robust video initialization for seeking
        if (!isVideoReadyForSeeking && video.readyState >= video.HAVE_METADATA) {
            initializeVideo();
        }
    }, [videoRef, isVideoReadyForSeeking, setCurrentTime, setDuration, initializeVideo]);

    const handleTimeUpdate = useCallback(() => {
        const video = videoRef?.current;
        if (!video) return;

        if (isDragging) return;

        syncTimeUpdate(video.currentTime);
    }, [isDragging, videoRef, syncTimeUpdate]);

    // Smooth timeline updates during playback using requestAnimationFrame
    useEffect(() => {
        const video = videoRef?.current;
        if (!video || !isPlaying) return;

        let rafId: number;
        const updateTimeline = () => {
            if (video && !video.paused && !isDragging) {
                syncTimeUpdate(video.currentTime);
                rafId = requestAnimationFrame(updateTimeline);
            }
        };

        rafId = requestAnimationFrame(updateTimeline);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [isPlaying, isDragging, syncTimeUpdate, videoRef]);

    const handlePlay = useCallback(() => {
        if (!isVideoReadyForSeeking) {
            setIsVideoReadyForSeeking(true);
        }
        onPlayClicked?.();
    }, [isVideoReadyForSeeking, onPlayClicked]);

    const handlePause = useCallback(
        (currentTime: number) => {
            onPauseClicked?.(currentTime);
        },
        [onPauseClicked]
    );

    useEffect(() => {
        const video = videoRef?.current;
        if (!video) return;

        const events = {
            loadedmetadata: handleLoadedMetadata,
            timeupdate: handleTimeUpdate,
            play: handlePlay,
            pause: () => handlePause(video.currentTime)
        };

        Object.entries(events).forEach(([event, handler]) => {
            video.addEventListener(event, handler);
        });

        const frameId = hoverThrottleRef.current;

        return () => {
            Object.entries(events).forEach(([event, handler]) => {
                video.removeEventListener(event, handler);
            });

            if (frameId) {
                cancelAnimationFrame(frameId);
            }
        };
    }, [videoRef, handlePause, handleLoadedMetadata, handlePlay, handleTimeUpdate]);

    return (
        <div className="flex w-full select-none flex-col md:gap-3">
            {duration > 0 && (
                <div className="flex items-center justify-end px-1 text-xs text-gray-400 md:text-sm">
                    <TimeDisplay currentTime={currentTime || 0} duration={duration} />
                </div>
            )}
            <div
                className={twClassNames(
                    "videoTimelineContainer relative",
                    flowLocked && "cursor-not-allowed opacity-50"
                )}
            >
                <div className="flex flex-shrink-0 items-center px-1">
                    <PlayPauseButton
                        isPlaying={isPlaying}
                        onClick={togglePlayPause}
                        disabled={flowLocked}
                    />
                    <VideoSpeedControl videoRef={videoRef} disabled={flowLocked} />
                    <VideoControlsMenu
                        videoRef={videoRef}
                        isPlaying={isPlaying}
                        disabled={flowLocked}
                        onTimelineClick={onTimelineClick}
                        setCurrentTime={setCurrentTime}
                        fps={videoFps}
                    />
                </div>

                <VideoControlsTimeline
                    videoRef={videoRef}
                    mediaItem={mediaItem}
                    isDragging={isDragging}
                    setIsDragging={setIsDragging}
                    flowLocked={flowLocked}
                    onPauseClicked={onPauseClicked}
                    onFramePreviewsReady={onFramePreviewsReady}
                    currentTime={currentTime || 0}
                    setCurrentTime={setCurrentTime}
                    isVideoReadyForSeeking={isVideoReadyForSeeking}
                    setIsVideoReadyForSeeking={setIsVideoReadyForSeeking}
                    onTimelineClick={onTimelineClick}
                    hoverThrottleRef={hoverThrottleRef}
                />
            </div>
        </div>
    );
};

VideoControls.displayName = "VideoControl";

export default VideoControls;
